import { FileImageOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Descriptions, Divider, Flex, Grid, Skeleton, Space, Table, Tabs, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Dispatch, Fragment, SetStateAction, useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getManpowerSupplyTicket } from 'src/apis/helpdesk/manpowerSupplyTicket.api';
import { getOvertimeTicket, supervisorConfirmOvertime } from 'src/apis/hr_ticket/overtimeTicket.api';
import { BtnHrHandle } from 'src/components/v2/Button';
import { HRTicketStatusTag } from 'src/components/v2/Tag/TicketStatusTag/TicketStatusTag';
import { ConfirmTypeEn } from 'src/constants/confirmTypeEn.enum';
import urlPath from 'src/constants/urlPath';
import { AppContext } from 'src/contexts/app.context';
import { HrTicketDetailContext } from 'src/contexts/hrTicketDetail.context';
import { TicketContext } from 'src/contexts/ticket.context';
import { useBasePath } from 'src/hooks/useBasePath';
import { TicketStatus } from 'src/types/helpdesk/ticket.type';
import { IBiometricDataLog } from 'src/types/hr_ticket/biometricDataLog.type';
import {
  IOvertimeAttachFile,
  OverTimeTicketConfirm,
  OvertimeDetailConfirmed,
  OvertimeTicketDetailResponse,
  OvertimeTicketProgress,
  ProjectAdd
} from 'src/types/hr_ticket/overtimeTicket.type';
import { IEmployeeShort } from 'src/types/master_data/employee.type';
import { showNotification } from 'src/utils/notification';
import { convertMinutesToHours, formatDate, formatTime, formatTimeString } from 'src/utils/utils';
import ManpowerSupplyTab from '../components/ManpowerSupplyTab/ManpowerSupplyTab';
import AddConfirmNote from './AddConfirmNote';
import ConfirmOvertimeTicket from './ConfirmOvertimeTicket';
import OvertimeTicketAttach from './OvertimeTicketAttach';
import './overtimeTicketDetail.scss';
const { Title, Text } = Typography;
export type OpenType = {
  isOpen: boolean;
  id?: number;
};

export type AttachOpenType = {
  isOpen: boolean;
  attachList?: IOvertimeAttachFile[];
};

const columns = function (isSupervisorConfirmed?: ConfirmTypeEn): ColumnsType<OvertimeDetailConfirmed> {
  const columnTitle: ColumnsType<OvertimeDetailConfirmed> = [
    {
      title: 'Người thực hiện',
      dataIndex: 'employee',
      key: 'employee',
      width: '25%',
      render: (value: IEmployeeShort, record) => (
        <Flex vertical gap={5}>
          <Text strong>{`${value.code} - ${value.fullName}`}</Text>
          {record.replaceForId && <Text>Làm thay: {record.replaceFor?.fullName}</Text>}
        </Flex>
      )
    },
    {
      title: 'Tổ',
      dataIndex: 'productionUnit',
      key: 'productionUnit',
      width: '20%',
      render: (_, record) => record?.productionUnit?.name
    },
    {
      title: 'TG. Tăng ca',
      dataIndex: '',
      key: 'time',
      width: '40%',
      align: 'left',
      render: (_, record) => (
        <Flex vertical gap={5}>
          <Descriptions className='overtime-ticket-time' layout='horizontal' labelStyle={{ width: '90px' }} column={1}>
            <Descriptions.Item label='Ngày'>
              <Text strong>{formatDate(record.date)}</Text>
            </Descriptions.Item>
            <Descriptions.Item label='Đăng ký'>
              <Flex gap={5} justify='center'>
                <Text>{formatTimeString(record.startTime, true) ?? ''}</Text>-
                <Text>{formatTimeString(record.endTime) ?? ''}</Text>
                <Text>({convertMinutesToHours(record.totalTime)})</Text>
              </Flex>
            </Descriptions.Item>
            <Descriptions.Item label='Chấm công'>
              <Flex gap={5} justify='center'>
                <Text>{formatTimeString(record?.checkInTime, true) ?? ''}</Text>-
                <Text>{formatTimeString(record?.checkOutTime) ?? ''}</Text>
              </Flex>
            </Descriptions.Item>
            <Descriptions.Item label='Xác nhận'>
              {isSupervisorConfirmed === ConfirmTypeEn.WaitingConfirm ? (
                <Text type='secondary'>Chưa xác nhận</Text>
              ) : !record.isSupervisorUnconfirmed ? (
                <Flex gap={5} justify='center'>
                  <Text strong type='success'>
                    {formatTimeString(record.actualStartTime, true) ?? ''}
                  </Text>
                  -
                  <Text strong type='success'>
                    {formatTimeString(record.actualEndTime) ?? ''}
                  </Text>
                  <Text type='success'>({convertMinutesToHours(record.actualTotalTime)})</Text>
                </Flex>
              ) : (
                <Text type='danger' strong>
                  Không xác nhận
                </Text>
              )}
            </Descriptions.Item>
          </Descriptions>
        </Flex>
      )
    },
    {
      title: 'Ghi chú',
      dataIndex: 'supervisorConfirmedNote',
      key: 'supervisorConfirmedNote',
      width: '15%',
      align: 'left'
    }
  ];

  return columnTitle;
};

const progressColumns = function (
  setOpenAttach: Dispatch<SetStateAction<AttachOpenType>>,
  isSupervisorConfirmed: number
): ColumnsType<OvertimeTicketProgress> {
  return [
    {
      title: 'Tên công việc',
      dataIndex: '',
      key: 'jobTitle',
      width: '15%',
      render: (record) => {
        return <Text>{record?.manpowerSupplyFormDetail?.jobTitle}</Text>;
      }
    },
    {
      title: 'Chi tiết công việc',
      dataIndex: '',
      key: 'jobDetail',
      width: '20%',
      align: 'left',
      render: (record) => <Text>{record?.manpowerSupplyFormDetail?.jobDetail}</Text>
    },
    {
      title: 'Yêu cầu đặc thù',
      dataIndex: '',
      key: 'jobRequirement',
      width: '20%',
      align: 'left',
      render: (record) => <Text>{record?.manpowerSupplyFormDetail?.jobRequirement}</Text>
    },

    {
      title: 'Tiến độ (%)',
      dataIndex: 'percentageCompletion',
      key: 'percentageCompletion',
      align: 'left',
      render: (_, record) => {
        if (isSupervisorConfirmed === ConfirmTypeEn.WaitingConfirm) {
          return <Text type='secondary'>Chưa xác nhận</Text>;
        }
        return (
          <Flex vertical>
            <Text>{record.percentageCompletion}%</Text>
            {record.isCompleted ? (
              <Text type='success' strong>
                Đã xác nhận
              </Text>
            ) : (
              <Text type='danger' strong>
                Không xác nhận
              </Text>
            )}
          </Flex>
        );
      }
    },
    {
      title: 'Ghi chú',
      dataIndex: 'note',
      key: 'note',
      width: '15%',
      align: 'left',
      render: (_, record) => {
        return <Text>{record.note}</Text>;
      }
    },
    {
      title: 'Action',
      dataIndex: '',
      key: 'action',
      width: '10%',
      align: 'left',
      render: (_, record) => {
        return (
          <Button
            size='small'
            icon={<FileImageOutlined />}
            onClick={() =>
              setOpenAttach((prev) => ({
                ...prev,
                isOpen: true,
                attachList: record.overtimeTicketProgressAttach as IOvertimeAttachFile[]
              }))
            }
          >
            Kết quả
          </Button>
        );
      }
    }
  ];
};

export default function OvertimeTicketDetail() {
  const { setInvalidKey } = useContext(TicketContext);
  const { setOvertimeTicket, setLoading } = useContext(HrTicketDetailContext);
  const { userInfo, ticketSelected } = useContext(AppContext);
  const [openConfirm, setOpenConfirm] = useState<OpenType>({ isOpen: false });
  const [openAttach, setOpenAttach] = useState<AttachOpenType>({ isOpen: false, attachList: [] });
  const [openNote, setOpenNote] = useState<boolean>(false);
  const [overtimeConfirms, setOvertimeConfirms] = useState<OvertimeDetailConfirmed[]>([]);
  const { id } = useParams();
  const queryClient = useQueryClient();
  const basePath = useBasePath();

  const { useBreakpoint } = Grid;
  const breakpoints = useBreakpoint();
  const colspan = breakpoints.xxl ? 2 : 1;

  const overtimeTicketQuery = useQuery({
    queryKey: ['overtimeTicket', id],
    queryFn: () => getOvertimeTicket(Number(id)),
    enabled: !!id && !!ticketSelected
  });

  const manpowerSupplyQuery = useQuery({
    queryKey: ['manpowerSupplyTicket', Number(overtimeTicketQuery.data?.data.manpowerSupplyId)],
    queryFn: () => getManpowerSupplyTicket(Number(overtimeTicketQuery.data?.data.manpowerSupplyId)),
    enabled: !!Number(overtimeTicketQuery.data?.data.manpowerSupplyId),
    staleTime: 60 * 1000
  });

  const isSupervisorConfirmed =
    basePath?.includes(urlPath.overtimeTicketSupervisorConfirm.location) &&
    overtimeTicketQuery.data?.data?.isSupervisorConfirmed === ConfirmTypeEn.WaitingConfirm &&
    overtimeTicketQuery.data?.data?.ticketStatusId === TicketStatus.ApprovedRequest &&
    overtimeTicketQuery.data?.data?.supervisorId === userInfo?.id;

  useEffect(() => {
    if (overtimeTicketQuery.data) {
      setOvertimeTicket(overtimeTicketQuery.data);
      setLoading(overtimeTicketQuery.isFetching);
      // Convert 3 lists confirm to 1 list
      const confirms = (overtimeTicketQuery.data?.data?.overtimeTicketDetails as OvertimeTicketDetailResponse[])?.map(
        (item) => {
          const biometricTime: IBiometricDataLog[] | undefined = overtimeTicketQuery.data?.data?.finalConfirms
            ?.find((finalItem) => finalItem.id === item.id)
            ?.biometricDataLogs?.sort((a, b) => new Date(a.record).getTime() - new Date(b.record).getTime());
          const confirmedItem = overtimeTicketQuery.data?.data?.overtimeTicketDetails?.find(
            (confirmedItem) =>
              confirmedItem.employeeId === item.employeeId &&
              confirmedItem.date === item.date &&
              confirmedItem.startTime === item.startTime &&
              confirmedItem.endTime === item.endTime
          );
          const projects = (item?.projects as ProjectAdd[])?.map((x) => x.projectCode);
          const checkinTime = biometricTime?.[0].record;
          const checkoutTime = biometricTime?.[biometricTime?.length - 1].record;

          return {
            ...item,
            projects: projects,
            startBiometricTime: formatTime(checkinTime),
            endBiometricTime: checkoutTime === checkinTime ? '' : formatTime(checkoutTime),
            checkInTime: confirmedItem?.checkInTime,
            checkOutTime: confirmedItem?.checkOutTime,
            isSupervisorUnconfirmed: confirmedItem?.isSupervisorUnconfirmed,
            actualStartTime: confirmedItem?.actualStartTime,
            totalTime: confirmedItem?.totalTime,
            actualTotalTime: confirmedItem?.actualTotalTime,
            actualEndTime: confirmedItem?.actualEndTime,
            supervisorConfirmedNote: overtimeTicketQuery.data.data.isSupervisorConfirmed
              ? confirmedItem?.supervisorConfirmedNote
              : ''
          };
        }
      );

      setOvertimeConfirms(confirms as OvertimeDetailConfirmed[]);

      setInvalidKey(JSON.stringify(overtimeTicketQuery.data?.data));
    }
  }, [overtimeTicketQuery.data, overtimeTicketQuery.isFetching, setOvertimeTicket, setLoading, setInvalidKey]);

  const supervisorNoConfirmBusinessTripMutation = useMutation({
    mutationFn: (overtimeTicketConfirm: OverTimeTicketConfirm) =>
      supervisorConfirmOvertime(Number(id), overtimeTicketConfirm),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['overtimeTicket', id] });
      showNotification('Từ chối xác nhận thành công', 'success');
    }
  });

  const handleAddNote = (note: string) => {
    supervisorNoConfirmBusinessTripMutation.mutate({
      isSupervisorConfirmed: ConfirmTypeEn.Reject,
      supervisorNote: note,
      compensatoryLeaveTickets: [],
      filePaths: [],
      confirms: [],
      unConfirmedDetails: []
    });
  };

  const tabItems = [
    {
      label: 'Phiếu tăng ca',
      key: '1',
      children: (
        <Fragment>
          <Flex justify='start'>
            <Title level={5}>Thông tin phiếu</Title>
          </Flex>
          {overtimeTicketQuery.isFetching ? (
            <Skeleton />
          ) : (
            <Fragment>
              <Descriptions
                className='overtime-ticket-item-header'
                layout='horizontal'
                labelStyle={{ width: '135px' }}
                column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 2 }}
              >
                <Descriptions.Item label='Mã phiếu'>
                  <Text strong>{overtimeTicketQuery.data?.data?.code}</Text>
                </Descriptions.Item>
                <Descriptions.Item label='Trạng thái'>
                  <HRTicketStatusTag
                    ticketStatus={overtimeTicketQuery.data?.data.ticketStatus}
                    erpKey={overtimeTicketQuery.data?.data.erpKey}
                    isSupervisorConfirmed={overtimeTicketQuery.data?.data.isSupervisorConfirmed}
                    isConfirm={overtimeTicketQuery.data?.data.isConfirm}
                    hrConfirmedType={overtimeTicketQuery.data?.data.hrConfirmedType}
                  />
                </Descriptions.Item>
                <Descriptions.Item label='Ticket raise' span={colspan}>
                  <Text strong>
                    <Link to={`${urlPath.myTicket.location}/${overtimeTicketQuery.data?.data?.refTicketIdLegacy}`}>
                      {overtimeTicketQuery.data?.data?.refTicketCode}
                    </Link>
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label='Loại tăng ca'>
                  <Text>{overtimeTicketQuery.data?.data?.overtimeType?.name}</Text>
                </Descriptions.Item>
                <Descriptions.Item label='Người giám sát' span={colspan}>
                  <Text strong>{overtimeTicketQuery.data?.data?.supervisor?.name}</Text>
                </Descriptions.Item>
                <Descriptions.Item label='Người tạo'>
                  <Text>{overtimeTicketQuery.data?.data?.createdBy?.fullName}</Text>
                </Descriptions.Item>
                <Descriptions.Item label='Ngày tạo' span={colspan}>
                  <Text strong>{formatDate(overtimeTicketQuery.data?.data?.createdDate)}</Text>
                </Descriptions.Item>
                <Descriptions.Item label='Bộ phận' span={colspan}>
                  <Text>{overtimeTicketQuery.data?.data?.createdBy?.department?.name}</Text>
                </Descriptions.Item>
                <Descriptions.Item label='Số phiếu ERP' span={colspan}>
                  {overtimeTicketQuery.data?.data?.erpKey}
                </Descriptions.Item>
                <Descriptions.Item label={'Lý do tăng ca'} span={colspan}>
                  <Text>{overtimeTicketQuery.data?.data?.note || ''}</Text>
                </Descriptions.Item>
              </Descriptions>
              <Divider style={{ marginTop: 16, marginBottom: 16 }} />

              <Flex vertical gap={5}>
                <Flex justify='space-between' align='center'>
                  <Title level={5} style={{ marginBottom: '0px' }}>
                    Kết quả công việc
                  </Title>
                  {isSupervisorConfirmed && (
                    <Space>
                      <Button
                        type='primary'
                        size='small'
                        onClick={() => setOpenConfirm({ isOpen: true, id: Number(id) ? Number(id) : undefined })}
                      >
                        Xác nhận kết quả công việc
                      </Button>
                    </Space>
                  )}
                </Flex>

                {overtimeTicketQuery.data?.data?.supervisorNote && (
                  <Text>{`Ghi chú: ${overtimeTicketQuery.data?.data?.supervisorNote || ''}`}</Text>
                )}
                <Table
                  className='overtime-progress-confirm-table'
                  columns={progressColumns(
                    setOpenAttach,
                    overtimeTicketQuery.data?.data?.isSupervisorConfirmed as number
                  )}
                  rowKey={(record) => String(record.manpowerSupplyFormDetailId)}
                  dataSource={overtimeTicketQuery.data?.data?.overtimeTicketProgresses}
                  pagination={false}
                  scroll={{ x: 400 }}
                  bordered={false}
                  size='middle'
                />
              </Flex>
              <Flex justify='space-between' align='center' className='overtime-detail-list-heading'>
                <Title level={5} style={{ marginBottom: '0px' }}>
                  Danh sách nhân lực tăng ca
                </Title>
              </Flex>
              <Table
                className='overtime-confirm-table'
                columns={columns(overtimeTicketQuery.data?.data.isSupervisorConfirmed)}
                rowKey={(record) => String(record.id)}
                dataSource={overtimeConfirms}
                pagination={false}
                scroll={{ x: 400 }}
                bordered={false}
                size='middle'
                expandable={{
                  expandedRowRender: (record) => (
                    <Descriptions
                      layout='horizontal'
                      labelStyle={{ width: '135px' }}
                      column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 2 }}
                    >
                      <Descriptions.Item label='Dự án thực hiện' span={colspan}>
                        {record.projects?.join(', ')}
                      </Descriptions.Item>
                      <Descriptions.Item label='Làm tại công trình'>
                        <Text strong>{record.isWorkAtConstructionSite ? 'Có' : 'Không'}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label='Đặt cơm'>
                        <Text strong>{record.isOrderMeal ? 'Có' : 'Không'}</Text>
                      </Descriptions.Item>
                    </Descriptions>
                  )
                }}
              />
            </Fragment>
          )}
        </Fragment>
      )
    },
    {
      label: 'Phiếu cung ứng nhân lực',
      key: '2',
      children: (
        <Fragment>
          {manpowerSupplyQuery.isFetching ? (
            <Skeleton active />
          ) : (
            <ManpowerSupplyTab manpowerSupply={manpowerSupplyQuery.data?.data} />
          )}
        </Fragment>
      )
    }
  ];

  return (
    <div className='overtime-ticket-detail-wrapper'>
      <BtnHrHandle type='overtimeTicket' />
      <Divider style={{ marginTop: 16, marginBottom: 16 }} />
      <Tabs defaultActiveKey='1' type='card' size='middle' items={tabItems} />
      {openConfirm.isOpen && (
        <ConfirmOvertimeTicket
          open={openConfirm}
          setOpen={setOpenConfirm}
          confirmsList={overtimeConfirms}
          jobConfirmsList={overtimeTicketQuery.data?.data?.overtimeTicketProgresses || []}
          date={`${formatDate(manpowerSupplyQuery.data?.data?.fromDate)}-${formatDate(
            manpowerSupplyQuery.data?.data?.toDate
          )}`}
          isSupervisorConfirmed={overtimeTicketQuery.data?.data?.isSupervisorConfirmed as number}
          ticketCode={String(overtimeTicketQuery.data?.data?.code)}
          overtimeType={overtimeTicketQuery.data?.data?.overtimeType?.code as string}
          handleDepartment={Number(overtimeTicketQuery.data?.data?.createdBy?.departmentId)}
        />
      )}
      {openAttach && <OvertimeTicketAttach open={openAttach} setOpen={setOpenAttach} />}
      {openNote && <AddConfirmNote open={openNote} setOpen={setOpenNote} handleAddNote={handleAddNote} />}
    </div>
  );
}
